<template>
  <div class="flex flex-col h-full min-h-0" @keydown.esc.stop="hideNow">
    <!-- Self row: your avatar + status picker (set status right from the list) -->
    <div class="flex items-center gap-2.5 px-3 py-2.5 border-b border-white/5">
      <ChatStatusPicker />
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-1.5 min-w-0">
          <p class="text-sm text-white font-semibold truncate">{{ myName }}</p>
          <span class="shrink-0 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/10 text-text-muted">
            {{ $t('chat.online.you') }}
          </span>
        </div>
        <p class="text-[10px] text-text-muted">{{ $t('chat.status.self.' + selfStatus) }}</p>
      </div>
      <span class="text-[10px] text-text-dim tabular-nums shrink-0">
        {{ $t('chat.online.count', { n: others.length }) }}
      </span>
    </div>

    <!-- Search -->
    <div class="px-3 py-2 border-b border-white/5">
      <div class="relative">
        <!-- Positioned by the span: .el-icon's position: relative beats Tailwind's `absolute`. -->
        <span class="absolute left-2.5 top-1/2 -translate-y-1/2 flex text-text-dim pointer-events-none"><el-icon size="13"><Search /></el-icon></span>
        <input
          v-model="q"
          type="text"
          :placeholder="$t('chat.online.search_placeholder')"
          class="w-full bg-black/30 border border-white/5 rounded-md pl-8 pr-2 py-1.5 text-xs text-white placeholder:text-text-dim focus:outline-none focus:border-primary/40"
        />
      </div>
    </div>

    <!-- List -->
    <div ref="listEl" class="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
      <div v-if="others.length === 0" class="text-center py-12 px-4">
        <p class="text-sm text-text-muted">{{ $t('chat.online.empty') }}</p>
      </div>

      <template v-for="group in groups" :key="group.key">
        <div
          class="sticky top-0 z-[1] flex items-center justify-between px-3 py-1.5 bg-[var(--chrome-bg,#0d0e12)]/80 backdrop-blur-sm"
        >
          <button
            type="button"
            class="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-text-muted font-semibold hover:text-white transition-colors"
            @click="toggleGroup(group.key)"
          >
            <el-icon size="10" class="transition-transform" :class="collapsed.has(group.key) ? '-rotate-90' : ''">
              <ArrowDown />
            </el-icon>
            {{ group.label }}
          </button>
          <span class="text-[10px] text-text-dim tabular-nums">{{ group.items.length }}</span>
        </div>

        <template v-if="!collapsed.has(group.key)">
          <button
            v-for="u in group.items"
            :key="u.id"
            type="button"
            class="online-row w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-white/[0.03] focus:bg-white/[0.05] focus:outline-none transition-colors"
            :data-uid="u.id"
            @mouseenter="onEnter(u.id, $event)"
            @mouseleave="scheduleHide"
            @focus="onEnter(u.id, $event)"
            @blur="scheduleHide"
            @keydown.down.prevent="focusSibling($event, 1)"
            @keydown.up.prevent="focusSibling($event, -1)"
            @click="onRowClick(u.id, $event)"
          >
            <div class="relative shrink-0" :class="{ 'collab-glow': u.openForCollabs }">
              <img
                :src="u.avatar || '/images/default-avatar.svg'"
                :alt="u.name"
                class="w-8 h-8 rounded-full object-cover"
                :class="u.openForCollabs ? 'ring-2 ring-cyan-400/70 ring-offset-1 ring-offset-transparent' : ''"
              />
              <span
                class="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-[var(--chrome-bg,#0d0e12)]"
                :class="dotClass(u.status)"
              ></span>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5 min-w-0">
                <p class="text-[13px] text-white font-medium truncate">{{ u.name }}</p>
                <span
                  v-if="isStaff(u.role)"
                  class="shrink-0 text-[8px] font-bold uppercase tracking-widest px-1 py-0.5 rounded bg-primary/15 text-primary"
                >{{ $t('chat.online.groups.staff') }}</span>
              </div>
              <p v-if="u.customStatus" class="text-[10px] text-text-dim italic truncate">{{ u.customStatus }}</p>
            </div>
          </button>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from 'vue'
import { Search, ArrowDown } from '@element-plus/icons-vue'
import ChatStatusPicker from '~/components/Chat/ChatStatusPicker.vue'
import { usePresence } from '~/composables/chat/usePresence'
import { useFriends } from '~/composables/chat/useFriends'
import { useHoverCard } from '~/composables/chat/useHoverCard'

const { t } = useI18n()
const { user } = useUserSession()
const { online, myId, selfStatus } = usePresence()
const { friends } = useFriends()
const { show, scheduleHide, pin, hideNow } = useHoverCard()

const myName = computed(() => user.value?.chatProfile?.displayName || user.value?.name || '')

const others = computed(() => online.value.filter(u => u.id !== myId.value))

const friendIds = computed(() => new Set((friends.value || []).map(f => f.user?.id).filter(Boolean)))

const q = ref('')
const filtered = computed(() => {
  const s = q.value.trim().toLowerCase()
  if (!s) return others.value
  return others.value.filter(u => (u.name || '').toLowerCase().includes(s))
})

function byName(a, b) { return (a.name || '').localeCompare(b.name || '') }

const groups = computed(() => {
  const fIds = friendIds.value
  const fr = [], on = [], afk = [], dnd = []
  for (const u of filtered.value) {
    if (fIds.has(u.id)) fr.push(u)
    else if (u.status === 'afk') afk.push(u)
    else if (u.status === 'dnd') dnd.push(u)
    else on.push(u)
  }
  fr.sort(byName); on.sort(byName); afk.sort(byName); dnd.sort(byName)
  return [
    { key: 'friends', label: t('chat.online.groups.friends'), items: fr },
    { key: 'online', label: t('chat.online.groups.online'), items: on },
    { key: 'afk', label: t('chat.online.groups.afk'), items: afk },
    { key: 'dnd', label: t('chat.online.groups.dnd'), items: dnd },
  ].filter(g => g.items.length > 0)
})

const collapsed = reactive(new Set())
function toggleGroup(key) {
  if (collapsed.has(key)) collapsed.delete(key)
  else collapsed.add(key)
}

/* ── Hover card wiring ── */
function onEnter(userId, e) {
  const el = e.currentTarget
  if (el) show(userId, el.getBoundingClientRect())
}
function onRowClick(userId, e) {
  // Click pins the card (works for touch where hover never fires); from there
  // the card's own buttons drive Message / Add-friend / etc.
  const el = e.currentTarget
  if (el) pin(userId, el.getBoundingClientRect())
}

/* ── Keyboard navigation (arrow up/down across rows) ── */
const listEl = ref(null)
function focusSibling(e, dir) {
  const rows = Array.from(listEl.value?.querySelectorAll('.online-row') || [])
  const idx = rows.indexOf(e.currentTarget)
  const next = rows[idx + dir]
  if (next) next.focus()
}

/* ── Helpers ── */
function isStaff(role) { return role === 'admin' || role === 'superadmin' }
function dotClass(status) {
  if (status === 'afk') return 'bg-amber-400'
  if (status === 'dnd') return 'bg-red-500'
  return 'bg-emerald-400'
}
</script>

<style scoped>
.scrollbar-thin::-webkit-scrollbar { width: 6px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.08); border-radius: 3px; }
.scrollbar-thin::-webkit-scrollbar-track { background: transparent; }

.collab-glow { animation: collabGlow 2.8s ease-in-out infinite; border-radius: 9999px; }
@keyframes collabGlow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 242, 255, 0.28); }
  50%      { box-shadow: 0 0 0 6px rgba(0, 242, 255, 0.00); }
}
</style>
